import { isCefrLevel, type CefrLevel, type WordState } from "@rmnlng/shared";
import { ChevronRight, Loader2, Play, Search, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useMe } from "@/lib/auth";
import { iconChip, useGrammarList, type GrammarListItem } from "@/lib/grammar";
import { useT, type I18nKey } from "@/lib/i18n";
import { useInfiniteScrollSentinel } from "@/lib/useInfiniteScrollSentinel";
import { cn } from "@/lib/utils";

// Discriminated union — mirrors WordsList. State chips and tag chips
// route through different API params, so the front-end shape carries
// that distinction explicitly. (CEFR-as-tag is a sub-case of `tag`
// that gets routed through `level` on the wire — see below.)
type Filter = { kind: "all" } | { kind: "state"; value: WordState } | { kind: "tag"; value: string };

const STATE_CHIPS: Array<{ state: WordState; labelKey: I18nKey }> = [
  { state: "learning", labelKey: "common.learning" },
  { state: "known", labelKey: "common.known" },
  { state: "new", labelKey: "common.new" },
];

const STATE_DOT: Record<WordState, string> = {
  known: "var(--good)",
  learning: "var(--accent)",
  new: "var(--text-muted)",
};

export function GrammarPage() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const t = useT();
  const { data: user } = useMe();

  // Filter state lives in the URL — fixes the long-standing bug where
  // `/grammar?tag=A0` (deep link from Levels page or hand-typed) was
  // ignored because the page never read search params. Also makes the
  // filter bookmarkable / shareable. Same pattern as WordsList.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = readFilterFromUrl(searchParams);

  function setFilter(next: Filter): void {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("state");
        p.delete("tag");
        if (next.kind === "state") p.set("state", next.value);
        if (next.kind === "tag") p.set("tag", next.value);
        return p;
      },
      { replace: true },
    );
  }

  // Hero is only meaningful in LEVEL mode + when the user isn't actively
  // searching/filtering (those flatten the dataset, breaking suggested
  // order). Open mode keeps the flat list as today.
  const isLevelMode = user?.studyMode === "LEVEL";
  const showHero = isLevelMode && !debounced && filter.kind === "all";

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(handle);
  }, [search]);

  // CEFR chips route through `level` on the API (not `tag`) so they
  // OVERRIDE the auto-fill from `currentLevel` in LEVEL mode. Without
  // this override, clicking the A2 chip while at A1 would AND
  // `level=A1 AND tag=A2` server-side and return nothing. Topical
  // tags stay on `tag` and remain level-scoped (which is desirable —
  // "A1 еда rules" is a more useful answer than "all еда rules").
  const cefrTagFilter = filter.kind === "tag" && isCefrLevel(filter.value) ? (filter.value as CefrLevel) : undefined;
  const topicalTagFilter = filter.kind === "tag" && !isCefrLevel(filter.value) ? filter.value : null;

  const { data, isPending, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useGrammarList({
    q: debounced || undefined,
    state: filter.kind === "state" ? filter.value : undefined,
    tag: topicalTagFilter ?? undefined,
    level: cefrTagFilter,
  });

  // Flatten the infinite-query pages for rendering; totals are identical
  // across pages so we just use page[0]. See WordsList for the same
  // pattern.
  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const totals = data?.pages[0]?.totals;
  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(fetchNextPage, !!hasNextPage && !isFetchingNextPage);

  // First un-known rule in the suggested order = "Up next." Items are
  // already in topo order from the API in LEVEL mode, so we just take
  // the first rule that isn't `known`.
  const upNext: GrammarListItem | undefined = showHero ? items.find((r) => r.state !== "known") : undefined;

  return (
    <div className="flex flex-col gap-3.5">
      <h1 className="text-[26px] font-bold tracking-[-0.02em]">{t("grammar.title")}</h1>

      <div className="text-[13px] text-text-3">
        {totals
          ? t("grammar.totals", {
              total: totals.total,
              known: totals.known,
              learning: totals.learning,
            })
          : "\u00A0"}
      </div>

      {/* "Up next" hero — visible only in LEVEL mode when the user
          isn't searching/filtering. Surfaces the suggested-order's
          first un-known rule as a focused entry point, so the user
          isn't presented with a 50-row menu (the overwhelm problem).
          The full list still renders below for browsing. */}
      {showHero && upNext && <UpNextHero rule={upNext} />}
      {showHero && !isPending && !upNext && totals && totals.total > 0 && user && (
        <UpNextAllDone level={user.currentLevel} />
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-text-3" />
        <Input
          className="pl-11"
          placeholder={t("grammar.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Filter chips — All, state chips, then dynamic CEFR levels
          pulled from `totals.levels` (only the levels with content
          render). Horizontally scrollable to fit narrow screens. */}
      <div className="scrollbar-hide -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1">
        <FilterChip active={filter.kind === "all"} onClick={() => setFilter({ kind: "all" })}>
          {t("common.all")}
        </FilterChip>
        {STATE_CHIPS.map((c) => (
          <FilterChip
            key={c.state}
            active={filter.kind === "state" && filter.value === c.state}
            onClick={() => setFilter({ kind: "state", value: c.state })}
          >
            {t(c.labelKey)}
          </FilterChip>
        ))}
        {totals?.levels.map((lvl) => (
          <FilterChip
            key={lvl}
            active={filter.kind === "tag" && filter.value === lvl}
            onClick={() => setFilter({ kind: "tag", value: lvl })}
          >
            {lvl}
          </FilterChip>
        ))}
      </div>

      {/* Topical-tag strip — surfaces when a non-CEFR tag is selected
          (e.g. deep-link from a rule's tag list). Clickable × clears
          back to "all". CEFR tags don't reach here; they're chips
          above. Same UX as WordsList. */}
      {topicalTagFilter && (
        <div className="flex items-center gap-2 text-[12px] text-text-3">
          <span>{t("grammar.filter_by_tag")}</span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 font-medium"
            style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
          >
            #{topicalTagFilter}
          </span>
          <button
            type="button"
            onClick={() => setFilter({ kind: "all" })}
            className="inline-flex items-center gap-0.5 text-text-3 hover:text-text"
            aria-label={t("grammar.clear_filter")}
          >
            <X className="size-3.5" />
            {t("grammar.clear_filter")}
          </button>
        </div>
      )}

      {isError && (
        <p className="text-[13px] text-[color:var(--again)]">
          {error instanceof Error ? error.message : t("common.failed_to_load")}
        </p>
      )}

      {isPending ? (
        <GrammarListSkeleton />
      ) : items.length === 0 ? (
        <div className="rounded-[var(--r-lg)] border border-dashed border-border bg-surface/40 px-4 py-10 text-center text-[13px] text-text-3">
          {debounced
            ? t("grammar.empty_no_match", { q: debounced })
            : filter.kind !== "all"
              ? t("grammar.empty_filter")
              : t("grammar.empty_all")}
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {items.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/grammar/${r.id}`}
                  className={cn(
                    "flex items-center gap-3 rounded-[var(--r-md)] border border-border bg-surface px-3.5 py-3.5 transition-colors hover:bg-[color:var(--surface-2)]",
                    // In LEVEL mode, known rules are dimmed — they're
                    // not the focus, but still browsable. Open mode
                    // shows them at full brightness (it's a flat list,
                    // no notion of "what's next").
                    isLevelMode && r.state === "known" && "opacity-60",
                  )}
                >
                  <div
                    className="flex size-9 shrink-0 items-center justify-center rounded-lg font-mono text-[13px] font-semibold"
                    style={{
                      background: "var(--accent-soft)",
                      color: "var(--accent)",
                    }}
                  >
                    {iconChip(r.title)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ background: STATE_DOT[r.state] }}
                        aria-label={r.state}
                      />
                      <span className="truncate text-[15px] font-semibold text-text">{r.title}</span>
                    </div>
                    {/* Summary wins over tags for the secondary line — it's
                        more informative. Tags still render below if a summary
                        is present (both fit; `summary` is usually ≤1 line). */}
                    {r.summary ? (
                      <div className="mt-0.5 truncate text-[12px] text-text-3">{r.summary}</div>
                    ) : (
                      r.tags.length > 0 && (
                        <div className="mt-0.5 truncate text-[12px] text-text-3">
                          {r.tags.map((tag) => `#${tag}`).join(" ")}
                        </div>
                      )
                    )}
                  </div>
                  <ChevronRight className="size-4 shrink-0 text-text-3" />
                </Link>
              </li>
            ))}
          </ul>
          {/* Sentinel + "loading more" spinner for infinite scroll — same
              pattern as WordsList. */}
          <div ref={sentinelRef} aria-hidden className="h-1" />
          {isFetchingNextPage && (
            <div className="flex items-center justify-center gap-2 py-4 text-[12px] text-text-3">
              <Loader2 className="size-3.5 animate-spin" />
              {t("common.loading_more")}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Hero card surfacing the "Up next" rule in suggested order. Distinct
// visual treatment from the list rows — gradient bg, primary CTA, an
// explicit "UP NEXT" eyebrow — so it reads as a focal entry point,
// not just another row.
function UpNextHero({ rule }: { rule: GrammarListItem }) {
  const t = useT();
  return (
    <div
      className="rounded-[var(--r-lg)] border-0 p-4 shadow-[var(--shadow-md)]"
      style={{ background: "linear-gradient(140deg, var(--accent-soft), var(--surface) 70%)" }}
    >
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[color:var(--accent)]">
        <Sparkles className="size-3.5" />
        {t("grammar.up_next")}
      </div>
      <Link to={`/grammar/${rule.id}`} className="block">
        <div className="flex items-start gap-3">
          <div
            className="flex size-10 shrink-0 items-center justify-center rounded-lg font-mono text-[14px] font-semibold"
            style={{ background: "var(--accent)", color: "var(--accent-fg)" }}
          >
            {iconChip(rule.title)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-semibold leading-tight">{rule.title}</div>
            {rule.summary && <div className="mt-1 line-clamp-2 text-[13px] text-text-2">{rule.summary}</div>}
          </div>
        </div>
      </Link>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Link
          to={`/grammar/${rule.id}`}
          className="inline-flex items-center justify-center gap-1.5 rounded-[var(--r-md)] border border-border bg-surface px-3 py-2 text-[13px] font-medium transition-colors hover:bg-[color:var(--surface-2)]"
        >
          {t("grammar.read")}
        </Link>
        <Link
          to="/study?mode=grammar"
          className="inline-flex items-center justify-center gap-1.5 rounded-[var(--r-md)] px-3 py-2 text-[13px] font-medium transition-opacity hover:opacity-90"
          style={{ background: "var(--accent)", color: "var(--accent-fg)" }}
        >
          <Play className="size-3.5" />
          {t("grammar.practice")}
        </Link>
      </div>
    </div>
  );
}

// Shown in place of the hero once every rule at the level is `known`.
// Celebratory + nudges toward the readiness exam (Phase 7 will wire
// the actual exam route; until then, the link goes to /levels).
function UpNextAllDone({ level }: { level: string }) {
  const t = useT();
  return (
    <div
      className="rounded-[var(--r-lg)] border-0 p-4 shadow-[var(--shadow-md)]"
      style={{ background: "linear-gradient(140deg, var(--good-soft), var(--surface) 70%)" }}
    >
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: "var(--good)" }}>
        {t("grammar.all_known_eyebrow", { level })}
      </div>
      {/*<div className="text-[15px] font-semibold">{t("grammar.all_known_title", { level })}</div>*/}
      <div className="mt-1 text-[13px] text-text-2">{t("grammar.all_known_body")}</div>
      <Link
        to="/levels"
        className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-[color:var(--accent)]"
      >
        {t("grammar.go_to_levels")} →
      </Link>
    </div>
  );
}

// Map URL search params to the typed Filter union. Unknown values fall
// through to "all" so a stale or hand-tampered URL gracefully shows the
// full list rather than throwing.
function readFilterFromUrl(params: URLSearchParams): Filter {
  const tag = params.get("tag");
  if (tag) return { kind: "tag", value: tag };
  const state = params.get("state");
  if (state === "learning" || state === "known" || state === "new") return { kind: "state", value: state };
  return { kind: "all" };
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex shrink-0 items-center rounded-full px-3 py-1.5 text-[12px] font-medium whitespace-nowrap transition-colors",
        active
          ? "bg-[color:var(--accent-soft)] text-[color:var(--accent)]"
          : "border border-border text-text-2 hover:bg-[color:var(--surface-2)]",
      )}
    >
      {children}
    </button>
  );
}

function GrammarListSkeleton() {
  return (
    <ul className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-[var(--r-md)] border border-border bg-surface px-3.5 py-3.5"
        >
          <Skeleton className="size-9 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Skeleton className="size-2 shrink-0 rounded-full" />
              <Skeleton className="h-4 w-44" />
            </div>
            <Skeleton className="mt-1.5 h-3 w-24" />
          </div>
        </li>
      ))}
    </ul>
  );
}
